import { useState, type ReactNode } from 'react';
import { useSession } from '../auth/session-context.js';
import { supportedTimezones } from '../lib/timezones.js';
import { SelectField } from '../ui/Field.js';

/**
 * Section 20: UTC on the wire and in storage, and the display zone is a user
 * preference with an instance default. This is where a person sets theirs.
 *
 * The list comes from the browser's own tz database rather than one we would have
 * to keep up to date. Where that is unavailable the fallback is small on purpose —
 * a short list someone can actually use beats a long one that is out of date.
 */

/** The sentinel for "no choice", since a `<select>` cannot hold null. */
const DEFAULT_VALUE = '__instance_default__';

export function TimezonePicker(): ReactNode {
  const { session, setTimezone } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (session === null) return null;
  const zones = supportedTimezones(session.user.timezone);

  const change = async (value: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await setTimezone(value === DEFAULT_VALUE ? null : value);
    } catch {
      setError('Could not save that. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SelectField
      label="Show times in"
      error={error}
      value={session.user.timezone ?? DEFAULT_VALUE}
      disabled={busy}
      onChange={(event) => {
        void change(event.target.value);
      }}
    >
      <option value={DEFAULT_VALUE}>
        This installation’s default ({session.instanceTimezone})
      </option>
      {zones.map((zone) => (
        <option key={zone} value={zone}>
          {zone}
        </option>
      ))}
    </SelectField>
  );
}
